import { Theme } from "@deere/fuel-react";
import EmailIcon from "@mui/icons-material/Email";
import { Box, IconButton, Popover, Tooltip, Typography } from "@mui/material";
import {
  GridColDef,
  GridColumnHeaderParams,
  GridRenderCellParams,
} from "@mui/x-data-grid-pro";
import { useOktaAuth } from "@okta/okta-react";
import { TFunction } from "i18next";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { GridTexts } from "./types";
import useDealerApi from "../../../../../api/dealer/useDealerApi";
import usePreferencesApi from "../../../../../api/preferences/usePreferencesApi";
import { BulkAlertPreferenceUser } from "../../../../preferences/types";

const D2D_EMAIL_PREFERENCE_CATEGORIES = [
  { category: "d2d_seller_order_requests", eventType: "NOTIFICATION" },
  { category: "d2d_buyer_order_status_updates", eventType: "NOTIFICATION" },
  { category: "all_alerts", eventType: "ALERT" },
] as const;

const EmailCell = ({
  accountId,
  dealerServiceUrl,
  theme,
}: {
  accountId: string;
  dealerServiceUrl: string;
  theme: Theme;
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [emails, setEmails] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const dealerApi = useDealerApi();
  const preferencesApi = usePreferencesApi();
  const { authState } = useOktaAuth();
  const { t } = useTranslation("d2d");

  const fetchEmails = async () => {
    if (emails !== null || loading) return;
    setLoading(true);
    setHasError(false);
    // Sent explicitly because ApiContextProvider (which normally adds it) is not mounted
    // when ENABLE_RBAC_AUTH_SUPPORT is off.
    const accessToken = authState?.accessToken?.accessToken;
    try {
      const userIds = await dealerApi.fetchDealerUserIds(
        dealerServiceUrl,
        accountId,
        accessToken
      );
      const found = await D2D_EMAIL_PREFERENCE_CATEGORIES.reduce(
        async (accPromise, { category, eventType }) => {
          const acc = await accPromise;
          if (acc !== null) return acc;
          const result: Record<string, BulkAlertPreferenceUser> =
            await preferencesApi.fetchBulkAlertPreferences(
              userIds,
              accountId,
              category,
              eventType,
              accessToken
            );
          const categoryEmails = [
            ...new Set(
              Object.values(result)
                .filter(
                  (u) => u.preference?.[category]?.channels?.email === true
                )
                .flatMap((u) => u.email_addresses)
            ),
          ];
          return categoryEmails.length > 0 ? categoryEmails : null;
        },
        Promise.resolve(null as string[] | null)
      );
      setEmails(found ?? []);
    } catch (error) {
      // Leave emails unset so the next click retries instead of caching a false "No email available".
      console.error(
        `Failed to fetch dealer emails for account ${accountId}:`,
        error
      );
      setHasError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    fetchEmails();
  };

  return (
    <>
      <Tooltip
        title={t(
          "otherDealerInventory.email.clickToViewEmails",
          "Click to view emails"
        )}
      >
        <span>
          <IconButton
            size="small"
            onClick={handleClick}
            sx={{ color: theme.tokens.FuelColorTextSecondary }}
          >
            <EmailIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
          {loading && (
            <Typography sx={{ fontSize: theme.tokens.FuelFontSizeSm }}>
              Loading...
            </Typography>
          )}
          {!loading && hasError && (
            <Typography sx={{ fontSize: theme.tokens.FuelFontSizeSm }}>
              {t(
                "otherDealerInventory.email.loadFailed",
                "Unable to load emails. Please try again."
              )}
            </Typography>
          )}
          {!loading && emails !== null && emails.length === 0 && (
            <Typography sx={{ fontSize: theme.tokens.FuelFontSizeSm }}>
              {t(
                "otherDealerInventory.email.noEmailsAvailable",
                "No email available"
              )}
            </Typography>
          )}
          {!loading &&
            emails?.map((email) => (
              <Typography
                key={email}
                sx={{
                  fontSize: theme.tokens.FuelFontSizeSm,
                  lineHeight: theme.tokens.FuelLineHeightMd,
                }}
              >
                {email}
              </Typography>
            ))}
        </Box>
      </Popover>
    </>
  );
};

export function columnDef(
  theme: Theme,
  gridTexts: GridTexts,
  dealerServiceUrl: string
): GridColDef[] {
  const renderCellStyle = {
    lineHeight: theme.tokens.FuelLineHeightMd,
    fontSize: theme.tokens.FuelFontSizeSm,
  };

  return [
    {
      field: "account",
      headerName: gridTexts.account,
      flex: 1,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Typography sx={renderCellStyle}>{params.value}</Typography>
      ),
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography
          sx={{
            fontWeight: theme.tokens.FuelFontWeightSemibold,
            fontSize: theme.tokens.FuelFontSizeHeading6,
          }}
        >
          {params.colDef.headerName}
        </Typography>
      ),
    },
    {
      field: "dealershipName",
      headerName: gridTexts.dealershipName,
      flex: 2,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Typography sx={renderCellStyle}>{params.value}</Typography>
      ),
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography
          sx={{
            fontWeight: theme.tokens.FuelFontWeightSemibold,
            fontSize: theme.tokens.FuelFontSizeHeading6,
          }}
        >
          {params.colDef.headerName}
        </Typography>
      ),
    },
    {
      field: "city",
      headerName: gridTexts.city,
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Typography sx={renderCellStyle}>{params.value}</Typography>
      ),
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography
          sx={{
            fontWeight: theme.tokens.FuelFontWeightSemibold,
            fontSize: theme.tokens.FuelFontSizeHeading6,
          }}
        >
          {params.colDef.headerName}
        </Typography>
      ),
    },
    {
      field: "state",
      headerName: gridTexts.state,
      flex: 1,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Typography sx={renderCellStyle}>{params.value}</Typography>
      ),
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography
          sx={{
            fontWeight: theme.tokens.FuelFontWeightSemibold,
            fontSize: theme.tokens.FuelFontSizeHeading6,
          }}
        >
          {params.colDef.headerName}
        </Typography>
      ),
    },
    {
      field: "country",
      headerName: gridTexts.country,
      flex: 1,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Typography sx={renderCellStyle}>{params.value}</Typography>
      ),
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography
          sx={{
            fontWeight: theme.tokens.FuelFontWeightSemibold,
            fontSize: theme.tokens.FuelFontSizeHeading6,
          }}
        >
          {params.colDef.headerName}
        </Typography>
      ),
    },
    {
      field: "phone",
      headerName: gridTexts.phone,
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Typography sx={renderCellStyle}>{params.value}</Typography>
      ),
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography
          sx={{
            fontWeight: theme.tokens.FuelFontWeightSemibold,
            fontSize: theme.tokens.FuelFontSizeHeading6,
          }}
        >
          {params.colDef.headerName}
        </Typography>
      ),
    },
    {
      field: "emails",
      headerName: gridTexts.email,
      flex: 1,
      minWidth: 200,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <EmailCell
          accountId={params.row.account as string}
          dealerServiceUrl={dealerServiceUrl}
          theme={theme}
        />
      ),
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography
          sx={{
            fontWeight: theme.tokens.FuelFontWeightSemibold,
            fontSize: theme.tokens.FuelFontSizeHeading6,
          }}
        >
          {params.colDef.headerName}
        </Typography>
      ),
    },
    {
      field: "inventoryBalance",
      headerName: gridTexts.inventoryBalance,
      flex: 1,
      minWidth: 150,
      align: "left",
      headerAlign: "left",
      renderCell: (params: GridRenderCellParams) => (
        <Typography sx={renderCellStyle}>{params.value}</Typography>
      ),
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography
          sx={{
            fontWeight: theme.tokens.FuelFontWeightSemibold,
            fontSize: theme.tokens.FuelFontSizeHeading6,
          }}
        >
          {params.colDef.headerName}
        </Typography>
      ),
    },
    // Intentionally omitting 'source', 'comment', and 'dateUpdated' columns per requirement to hide them.
  ];
}

export const getGridTexts = (
  t: TFunction<"translation", undefined>
): GridTexts => ({
  account: t("d2d:otherDealerInventory.grid.account", "Account"),
  dealershipName: t(
    "d2d:otherDealerInventory.grid.dealershipName",
    "Dealership Name"
  ),
  city: t("d2d:otherDealerInventory.grid.city", "City"),
  state: t("d2d:otherDealerInventory.grid.state", "State"),
  country: t("d2d:otherDealerInventory.grid.country", "Country"),
  phone: t("d2d:otherDealerInventory.grid.phone", "Phone"),
  email: t("d2d:otherDealerInventory.grid.email", "Email"),
  inventoryBalance: t(
    "d2d:otherDealerInventory.grid.inventoryBalance",
    "Inventory Balance"
  ),
  source: t("d2d:otherDealerInventory.grid.source", "Source"),
  comment: t("d2d:otherDealerInventory.grid.comment", "Comment"),
});
